/**
 * Eigentümerversammlungs-Sektion im WEG-Property-Tab (Phase 5).
 * Listet alle Versammlungen des Objekts. Neu → Deep-Link zum Editor.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, Plus, ArrowRight, CalendarCheck, Warning, CheckCircle } from "@phosphor-icons/react";

const STATUS_META = {
  planned:   { label: "Geplant",     color: "bg-slate-100 text-slate-700" },
  invited:   { label: "Eingeladen",  color: "bg-amber-100 text-amber-800" },
  held:      { label: "Durchgeführt", color: "bg-blue-100 text-blue-800" },
  closed:    { label: "Abgeschlossen", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Abgesagt",    color: "bg-slate-100 text-slate-500" },
};

export default function AssemblySection({ property }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: `Ordentliche Eigentümerversammlung ${new Date().getFullYear() + 1}`,
    assembly_date: "", location: "", start_time: "18:00",
  });

  const load = () =>
    api.get(`/weg/${property.id}/assemblies`)
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [property.id]);

  const create = async () => {
    if (!form.title.trim() || !form.assembly_date) {
      toast.error("Titel und Datum sind Pflicht");
      return;
    }
    try {
      const r = await api.post(`/weg/${property.id}/assemblies`, {
        property_id: property.id,
        title: form.title.trim(),
        assembly_date: form.assembly_date,
        location: form.location || null,
        start_time: form.start_time || null,
        quorum_required_pct: 50,
        agenda_items: [],
        attendance: [],
      });
      toast.success(`Versammlung Nr. ${r.data.assembly_number} angelegt`);
      setOpen(false);
      nav(`/weg/versammlung/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    }
  };

  return (
    <div className="space-y-3" data-testid="assembly-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Users weight="duotone" className="text-emerald-700" /> Eigentümerversammlungen
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Planen, einladen (3-Wochen-Frist § 24 Abs. 4 WEG), Präsenz erfassen, abstimmen, Protokoll führen.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="assembly-add-btn">
              <Plus size={13} className="mr-1.5" /> Neue Versammlung
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Neue Eigentümerversammlung</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <label className="text-xs text-slate-600 block">Titel *
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assembly-title-input" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-slate-600 block">Datum *
                  <Input type="date" value={form.assembly_date} onChange={(e) => setForm({ ...form, assembly_date: e.target.value })} data-testid="assembly-date-input" />
                </label>
                <label className="text-xs text-slate-600 block">Beginn
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
                </label>
              </div>
              <label className="text-xs text-slate-600 block">Ort
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="z. B. Gemeinschaftsraum" />
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={create} data-testid="assembly-create-btn">Anlegen &amp; öffnen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center bg-slate-50 text-sm text-slate-500" data-testid="assembly-empty">
          Noch keine Eigentümerversammlung geplant.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const st = STATUS_META[r.status] || STATUS_META.planned;
            return (
              <li key={r.id} data-testid={`assembly-item-${r.assembly_number}`}>
                <Link
                  to={`/weg/versammlung/${r.id}`}
                  className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:border-emerald-400 hover:shadow-sm transition"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-500">Nr. {r.assembly_number}</span>
                      <Badge className={`text-[10px] ${st.color}`}>{st.label}</Badge>
                      {r.invited_at && r.invitation_deadline_met === false && (
                        <Badge className="bg-rose-100 text-rose-800 text-[10px]">
                          <Warning size={10} weight="fill" className="inline mr-0.5" /> Frist verpasst
                        </Badge>
                      )}
                      {r.invited_at && r.invitation_deadline_met && (
                        <Badge variant="outline" className="text-[10px]">
                          <CheckCircle size={10} className="inline mr-0.5" /> Frist gewahrt
                        </Badge>
                      )}
                    </div>
                    <div className="font-medium text-slate-900 mt-0.5">{r.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <CalendarCheck size={11} /> {r.assembly_date}
                      {r.start_time && <span>· {r.start_time}</span>}
                      {r.location && <span>· {r.location}</span>}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-slate-400" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
